import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import {
  Card,
  CardHeader,
  CardContent,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  Box,
  Tooltip,
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import dayjs from 'dayjs';

import * as actions from '../../redux/actions';
import UpdateLabourTimestamp from '../../components/Forms/UpdateLabourTimestamp';
import api from '../../utils/functions/api';
import { useSocket } from '../../contexts/SocketContext';

const ACTION_COLOR = {
  CREATE: 'success',
  UPDATE: 'warning',
  DELETE: 'error',
};

export default function Developer() {
  const dispatch = useDispatch();
  const socket = useSocket();

  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncError, setSyncError] = useState('');
  const [syncConfirm, setSyncConfirm] = useState(false);

  const [form, setForm] = useState({
    userId: '',
    salary_month: '',
    salary_day: '',
    salary_hour: '',
  });
  const [extraForm, setExtraForm] = useState({
    month: '',
    day: '',
  });

  const [activityRows, setActivityRows] = useState([]);
  const [activityCount, setActivityCount] = useState(0);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityModule, setActivityModule] = useState('');
  const [activityPage, setActivityPage] = useState(1);
  const activityPageSize = 50;

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const [activityError, setActivityError] = useState('');
  const [allEmployees, setAllEmployees] = useState([]);

  const [onlineUsers, setOnlineUsers] = useState([]);
  const [onlineLoading, setOnlineLoading] = useState(false);
  const [onlineLastFetch, setOnlineLastFetch] = useState(null);

  const apiBase = String(process.env.REACT_APP_API_URL || '').trim();

  const fetchOnlineUsers = useCallback(async () => {
    setOnlineLoading(true);
    try {
      const { data } = await api.get(`${apiBase}/user/online`);
      setOnlineUsers(Array.isArray(data) ? data : []);
      setOnlineLastFetch(Date.now());
    } catch {
      // silently ignore
    } finally {
      setOnlineLoading(false);
    }
  }, [apiBase]);

  const fetchActivityLog = useCallback(async () => {
    setActivityLoading(true);
    setActivityError('');
    try {
      const qs = new URLSearchParams({
        page: String(activityPage),
        size: String(activityPageSize),
      });
      if (activityModule) qs.set('module', activityModule);
      const { data, status } = await api.get(
        `${apiBase}/activity-log?${qs.toString()}`,
      );
      if (status === 200 && data) {
        setActivityRows(data.rows || []);
        setActivityCount(data.count ?? 0);
      }
    } catch (e) {
      console.error(e);
      const msg =
        e?.response?.data?.error?.message ||
        e?.response?.data?.message ||
        e?.message ||
        'โหลด log ไม่สำเร็จ';
      setActivityError(String(msg));
      setActivityRows([]);
      setActivityCount(0);
    } finally {
      setActivityLoading(false);
    }
  }, [activityModule, activityPage, apiBase]);

  useEffect(() => {
    const fetchAllEmployees = async () => {
      const PAGE_SIZE = 50;
      let pg = 1;
      let accumulated = [];
      let hasMore = true;
      while (hasMore) {
        const { data } = await api.get(
          `${apiBase}/employee?name=&size=${PAGE_SIZE}&page=${pg}&activeOnly=false`,
        );
        const rows = data?.rows || [];
        const total = data?.total || 0;
        accumulated = [...accumulated, ...rows];
        setAllEmployees([...accumulated]);
        hasMore = accumulated.length < total && rows.length > 0;
        pg++;
      }
    };
    fetchAllEmployees();
  }, [apiBase]);

  useEffect(() => {
    fetchActivityLog();
  }, [fetchActivityLog]);

  useEffect(() => {
    fetchOnlineUsers();
    const id = setInterval(fetchOnlineUsers, 30 * 1000); // fallback poll ทุก 30 วินาที
    return () => clearInterval(id);
  }, [fetchOnlineUsers]);

  // real-time update ผ่าน Socket.IO
  useEffect(() => {
    if (!socket) return;
    const handler = (list) => {
      setOnlineUsers(Array.isArray(list) ? list : []);
      setOnlineLastFetch(Date.now());
    };
    socket.on('online:update', handler);
    return () => socket.off('online:update', handler);
  }, [socket]);

  const onSave = () => {
    const payload = {
      salary: {
        month: Number(form.salary_month),
        day: Number(form.salary_day),
        hr: Number(form.salary_day / 8),
      },
    };
    dispatch(actions.timestampPutSalary(form.userId, payload));
  };

  const onSaveExtra = () => {
    const payload = {
      salary_extra: {
        month: Number(extraForm.month),
        day: Number(extraForm.day),
      },
    };
    dispatch(actions.timestampPutSalary(form.userId, payload));
  };

  const runSync = async () => {
    setSyncConfirm(false);
    setSyncLoading(true);
    setSyncResult(null);
    setSyncError('');
    try {
      const { data } = await api.post(`${apiBase}/sync-dev-db`);
      setSyncResult(data);
    } catch (e) {
      setSyncError(
        e?.response?.data?.error?.message || e?.message || 'Sync ไม่สำเร็จ',
      );
    } finally {
      setSyncLoading(false);
    }
  };

  const renderSyncDevDb = () => (
    <Card sx={{ mb: 3, border: '1px solid #fca5a5' }} variant="outlined">
      <CardHeader
        title="Sync Dev Database"
        subheader="คัดลอกข้อมูลจาก Production → Atlas Dev (coreplan_dev) — ข้อมูลเดิมใน Dev จะถูกลบทั้งหมด"
        titleTypographyProps={{ color: '#dc2626' }}
      />
      <CardContent>
        {syncError && (
          <Typography color="error" sx={{ mb: 2 }}>
            {syncError}
          </Typography>
        )}

        {syncResult && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#15803d' }}>
              Sync สำเร็จ — {syncResult.total?.toLocaleString()} documents
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 260, overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Collection</TableCell>
                    <TableCell align="right">Documents</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(syncResult.collections || []).map((r) => (
                    <TableRow key={r.collection}>
                      <TableCell>{r.collection}</TableCell>
                      <TableCell align="right">{r.count?.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        <Button
          variant="contained"
          color="error"
          disabled={syncLoading}
          onClick={() => setSyncConfirm(true)}
          startIcon={syncLoading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {syncLoading ? 'กำลัง Sync...' : 'เริ่ม Sync'}
        </Button>
      </CardContent>
    </Card>
  );

  const openDetail = (row) => {
    setDetailRow(row);
    setDetailOpen(true);
  };

  const renderOnlineUsers = () => {
    const onlineList = onlineUsers.filter((u) => u.isOnline);
    const recentList = onlineUsers.filter((u) => !u.isOnline);
    const totalOnline = onlineList.length;

    return (
      <Card sx={{ mb: 3 }} variant="outlined">
        <CardHeader
          title={
            <Stack direction="row" alignItems="center" spacing={1}>
              <FiberManualRecordIcon sx={{ color: totalOnline > 0 ? '#22c55e' : '#94a3b8', fontSize: 14 }} />
              <Typography variant="h6" component="span">
                ผู้ใช้งานในระบบ
              </Typography>
              {totalOnline > 0 && (
                <Chip label={`${totalOnline} online`} size="small" sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 700, fontSize: '0.72rem' }} />
              )}
            </Stack>
          }
          subheader="แสดงเฉพาะผู้ที่มี request จริง (active < 3 นาที) และเคย active ใน 1 ชั่วโมงที่ผ่านมา"
          action={
            <Stack direction="row" alignItems="center" spacing={1}>
              {onlineLastFetch && (
                <Typography variant="caption" color="text.secondary">
                  อัปเดต {dayjs(onlineLastFetch).format('HH:mm:ss')}
                </Typography>
              )}
              <Button size="small" onClick={fetchOnlineUsers} disabled={onlineLoading}>
                {onlineLoading ? <CircularProgress size={14} /> : 'รีเฟรช'}
              </Button>
            </Stack>
          }
        />
        <CardContent sx={{ pt: 0 }}>
          {onlineUsers.length === 0 && !onlineLoading && (
            <Typography variant="body2" color="text.secondary">ไม่มีผู้ใช้งานในขณะนี้</Typography>
          )}

          {/* กำลัง online */}
          {onlineList.length > 0 && (
            <Box sx={{ mb: recentList.length > 0 ? 2 : 0 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#15803d', mb: 0.5, display: 'block' }}>
                กำลังใช้งาน
              </Typography>
              <Stack spacing={0.5}>
                {onlineList.map((u) => (
                  <Box
                    key={u.userId}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      py: 0.75,
                      bgcolor: '#f0fdf4',
                      borderRadius: 1.5,
                      border: '1px solid #bbf7d0',
                    }}
                  >
                    <FiberManualRecordIcon sx={{ color: '#22c55e', fontSize: 10 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>{u.username}</Typography>
                    <Tooltip title={u.ip || ''}>
                      <Typography variant="caption" color="text.secondary">
                        active เมื่อกี้
                      </Typography>
                    </Tooltip>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {/* เคย active แต่เลิกแล้ว (< 1 ชั่วโมง) */}
          {recentList.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#94a3b8', mb: 0.5, display: 'block' }}>
                เลิกใช้งานแล้ว
              </Typography>
              <Stack spacing={0.5}>
                {recentList.map((u) => (
                  <Box
                    key={u.userId}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      py: 0.75,
                      bgcolor: '#f8fafc',
                      borderRadius: 1.5,
                      border: '1px solid #e2e8f0',
                    }}
                  >
                    <FiberManualRecordIcon sx={{ color: '#cbd5e1', fontSize: 10 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>{u.username}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {u.minutesAgo < 1 ? 'เมื่อกี้' : `เลิกใช้งาน ${u.minutesAgo} นาทีที่แล้ว`}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderUpdateSalary = () => (
    <div>
      <UpdateLabourTimestamp
        employee={allEmployees}
        form={form}
        setForm={setForm}
        onSave={onSave}
        onSaveExtra={onSaveExtra}
        setExtraForm={setExtraForm}
        extraForm={extraForm}
      />
    </div>
  );

  return (
    <div>
      {renderSyncDevDb()}
      {renderOnlineUsers()}
      {renderUpdateSalary()}

      <Card sx={{ mt: 3 }} variant="outlined">
        <CardHeader
          title="บันทึกกิจกรรม HR / Finance"
          subheader="ย้อนหลังการสร้าง แก้ไข ลบรายการลงเวลาและค่าใช้จ่าย (รวม IP และ user จาก token เมื่อมี)"
        />
        <CardContent>
          {activityError ? (
            <Typography color="error" sx={{ mb: 2 }}>
              {activityError}
              {apiBase ? '' : ' (ยังไม่ได้ตั้ง REACT_APP_API_URL)'}
            </Typography>
          ) : null}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              select
              label="โมดูล"
              size="small"
              value={activityModule}
              onChange={(e) => {
                setActivityPage(1);
                setActivityModule(e.target.value);
              }}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">ทั้งหมด</MenuItem>
              <MenuItem value="HR">HR</MenuItem>
              <MenuItem value="FINANCE">Finance</MenuItem>
            </TextField>
            <Button variant="outlined" onClick={fetchActivityLog} disabled={activityLoading}>
              รีเฟรช
            </Button>
            <Typography variant="body2" sx={{ alignSelf: 'center' }}>
              ทั้งหมด {activityCount} รายการ
            </Typography>
          </Stack>

          {activityLoading ? (
            <Stack alignItems="center" py={4}>
              <CircularProgress size={32} />
            </Stack>
          ) : (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                maxHeight: 420,
                overflow: 'auto',
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>เวลา</TableCell>
                    <TableCell>โมดูล</TableCell>
                    <TableCell>ทรัพยากร</TableCell>
                    <TableCell>การกระทำ</TableCell>
                    <TableCell>ผู้ทำ (user)</TableCell>
                    <TableCell>สรุป</TableCell>
                    <TableCell align="right">รายละเอียด</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activityRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography color="text.secondary">ยังไม่มี log</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    activityRows.map((row) => (
                      <TableRow key={row._id}>
                        <TableCell>
                          {row.createdAt
                            ? dayjs(row.createdAt).format('DD/MM/YYYY HH:mm:ss')
                            : '-'}
                        </TableCell>
                        <TableCell>{row.module}</TableCell>
                        <TableCell>{row.resourceType}</TableCell>
                        <TableCell>
                          <Chip
                            label={row.action}
                            color={ACTION_COLOR[row.action] || 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {row.actorUsername || '-'}
                          {row.ip ? (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {row.ip}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 360 }}>
                          <Typography variant="body2" noWrap title={row.summary}>
                            {row.summary || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Button size="small" onClick={() => openDetail(row)}>
                            JSON
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {activityCount > activityPageSize ? (
            <Stack direction="row" spacing={2} justifyContent="center" mt={2}>
              <Button
                size="small"
                disabled={activityPage <= 1}
                onClick={() => setActivityPage((p) => Math.max(1, p - 1))}
              >
                ก่อนหน้า
              </Button>
              <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                หน้า {activityPage}
              </Typography>
              <Button
                size="small"
                disabled={activityPage * activityPageSize >= activityCount}
                onClick={() => setActivityPage((p) => p + 1)}
              >
                ถัดไป
              </Button>
            </Stack>
          ) : null}
        </CardContent>
      </Card>

      <Dialog open={syncConfirm} onClose={() => setSyncConfirm(false)}>
        <DialogTitle>ยืนยันการ Sync</DialogTitle>
        <DialogContent>
          <Typography>
            ข้อมูลทั้งหมดใน <strong>coreplan_dev</strong> จะถูกลบและแทนที่ด้วยข้อมูลจาก Production ใช่ไหม?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncConfirm(false)}>ยกเลิก</Button>
          <Button variant="contained" color="error" onClick={runSync}>
            ยืนยัน Sync
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>รายละเอียด log</DialogTitle>
        <DialogContent dividers>
          {detailRow ? (
            <Typography
              component="pre"
              sx={{
                fontFamily: 'monospace',
                fontSize: 12,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {JSON.stringify(
                {
                  _id: detailRow._id,
                  module: detailRow.module,
                  resourceType: detailRow.resourceType,
                  action: detailRow.action,
                  resourceId: detailRow.resourceId,
                  summary: detailRow.summary,
                  actorUserId: detailRow.actorUserId,
                  actorUsername: detailRow.actorUsername,
                  ip: detailRow.ip,
                  userAgent: detailRow.userAgent,
                  before: detailRow.before,
                  after: detailRow.after,
                  meta: detailRow.meta,
                  createdAt: detailRow.createdAt,
                },
                null,
                2,
              )}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>ปิด</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
